import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
  TextField,
  FormControlLabel,
  Checkbox,
  Box
} from '@mui/material';
import { Demandes } from '../models/Demandes';
import { BASE_URL } from '../api/config';
import useIsMobile from '../hooks/useIsMobile';

interface DemandeActionsProps {
  open: boolean;
  onClose: () => void;
  onValidated: () => void;
  selectedIds: number[];
  selectedEmail: string | null;
  selectedDemandes: Demandes[]; // On reçoit ici la liste des demandes sélectionnées
}

const DemandeActions: React.FC<DemandeActionsProps> = ({
  open,
  onClose,
  onValidated,
  selectedIds,
  selectedEmail,
  selectedDemandes
}) => {
  const [objetMail, setObjetMail] = useState('');
  const [alerte, setAlerte] = useState(false);
  const [envoyerMail, setEnvoyerMail] = useState(true);
  const [messageAppli, setMessageAppli] = useState('');
  const [messageMail, setMessageMail] = useState('');
  const [localEmail, setLocalEmail] = useState(selectedEmail || '');
  const [currentActionType, setCurrentActionType] = useState<string>('');
  const isMobile = useIsMobile();

  // Permet de remplacer les sauts de ligne par <br> pour l'envoi HTML
  const formattedMessageMail = messageMail.replace(/\n/g, '<br>');

  // Remise à zéro / initialisation lorsque la modal s'ouvre ou se ferme
  useEffect(() => {
    if (!open) {
      setObjetMail('');
      setAlerte(false);
      setEnvoyerMail(true);
      setMessageAppli('');
      setMessageMail('');
      setCurrentActionType('');
    } else {
      // Si plusieurs demandes, on peut combiner l'objet
      if (selectedDemandes.length === 1) {
        const d = selectedDemandes[0];
        setObjetMail(`Dossier ${d.id}`);
      } else if (selectedDemandes.length > 1) {
        setObjetMail(`Dossiers: ${selectedIds.join(', ')}`);
      }

      // Récupérer et concaténer infosdemande si présent
      // (Si plusieurs demandes, on ajoute chaque infosdemande séparément)
      const combinedInfos = selectedDemandes
        .map((d) => d.infosdemande)
        .filter(Boolean) // retire les undefined / chaînes vides
        .join('\n');

      if (combinedInfos) {
        setMessageAppli(combinedInfos);
      }
    }
  }, [open, selectedDemandes, selectedIds]);

  useEffect(() => {
    if (open) {
      setLocalEmail(selectedEmail || '');
    }
  }, [open, selectedEmail]);

  const handleValidation = async () => {
    const dataToSend = {
      ids: selectedIds,
      email: localEmail,
      objet: objetMail,
      alerte,
      envoyerMail,
      messageAppli,
      messageMail: formattedMessageMail,
      actionType: currentActionType
    };

    await fetch(`${BASE_URL}/actionsDemande`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dataToSend),
    });

    onClose();
    onValidated();
  };

  /**
   * Vérifie si c'est un eVisa ou un Visa
   * On détermine ça à partir de `demande.titre`
   */
  const isEvisa = (titre: string): boolean => {
    return titre.toLowerCase().includes('evisa');
  };

  /**
   * Construit la chaîne pour l'objet de l'email selon la demande
   * ex: "Nom + prénom - Dossier n°dossier - Action requise ..."
   */
  const buildObject = (demande: Demandes, suffix: string) => {
    return `${demande.nom} ${demande.prenom} - Dossier ${demande.id} - ${suffix}`;
  };

  /**
   * Remplit le mail problème (Visa ou eVisa)
   */
  const fillProblemMessage = () => {
    if (selectedDemandes.length === 0) return;

    let finalSubject = '';
    let finalBody = '';

    selectedDemandes.forEach((demande, index) => {
      const subjectPart = buildObject(
        demande,
        'Action requise : Informations ou pièces justificatives erronées dans votre dossier'
      );

      finalSubject += subjectPart;
      if (index < selectedDemandes.length - 1) {
        finalSubject += ' | ';
      }

      const body = `
Bonjour ${demande.prenom},

Après examen de votre dossier, nous constatons certaines informations ou pièces justificatives non conformes. 
Afin de compléter votre dossier et de poursuivre le traitement, merci de bien vouloir apporter les modifications nécessaires. 

Détails des éléments à corriger : 
(Nom du document/information) : (Description de l'erreur ou du problème rencontré) 
(Nom du document/information) : (Description de l'erreur ou du problème rencontré) 
(Nom du document/information) : (Description de l'erreur ou du problème rencontré) 

Actions à entreprendre : Merci de bien vouloir nous faire parvenir en réponse à ce mail les éléments cités ci-dessus afin que nos équipes corrigent votre dossier et traitent votre demande.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---

`;
      finalBody += body;
    });

    setObjetMail(finalSubject);
    setMessageMail(finalBody);
    setCurrentActionType('visa_probleme');
  };

  /**
   * Mail "visa en cours"
   */
  const fillInProgressMessage = () => {
    if (selectedDemandes.length === 0) return;

    let finalSubject = '';
    let finalBody = '';

    selectedDemandes.forEach((demande, index) => {
      const subjectPart = buildObject(demande, 'Votre dossier est complet');
      finalSubject += subjectPart;
      if (index < selectedDemandes.length - 1) {
        finalSubject += ' | ';
      }

      if (isEvisa(demande.titre)) {
        // eVisa
        const body = `
Bonjour ${demande.prenom},

Votre dossier a été vérifié par nos services, il est conforme aux attentes des services consulaires concernés.

Nos agents procéderont à la demande officielle selon le délai choisi lors de votre commande : 
Sous 48h ouvrées pour l’option premium
Le jour même pour l’option VIP (du lundi au vendredi)

Si vous avez choisi l’option premium et souhaitez accélérer le processus, merci de prendre contact avec nos services au plus vite.
Vous serez tenu informé de l’évolution de votre demande à chaque étape.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---

`;
        finalBody += body;
      } else {
        // Visa
        const body = `
Bonjour ${demande.prenom},

Votre dossier a été vérifié par nos services et sera présenté au plus vite auprès des services consulaires concernés.

Vous serez tenu informé de l’évolution de votre demande à chaque étape.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---

`;
        finalBody += body;
      }
    });

    setObjetMail(finalSubject);
    setMessageMail(finalBody);
    setCurrentActionType('visa_en_cours');
  };

  /**
   * Mail "visa plus tard"
   */
  const fillVisaLaterMessage = () => {
    if (selectedDemandes.length === 0) return;

    let finalSubject = '';
    let finalBody = '';

    selectedDemandes.forEach((demande, index) => {
      const subjectPart = buildObject(demande, 'Votre dossier est complet');
      finalSubject += subjectPart;
      if (index < selectedDemandes.length - 1) {
        finalSubject += ' | ';
      }

      const body = `
Bonjour ${demande.prenom},

Votre dossier a été vérifié par nos services, il est conforme aux attentes des services consulaires concernés.

Nous constatons que votre date de départ ne permet pas d’effectuer la demande dès maintenant sur le site officiel de demande de visa. 

En effet, si la demande était créée maintenant, le visa ne serait plus valable pour votre date de départ.

Pas d’inquiétude, votre dossier est conservé précieusement par nos agents et sera traité dès que la validité du visa permettra de le faire.

Vous serez tenu informé de l’évolution de votre demande à chaque étape.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---

`;
      finalBody += body;
    });

    setObjetMail(finalSubject);
    setMessageMail(finalBody);
    setCurrentActionType('visa_plus_tard');
  };

  /**
   * Mail "Adresse de réexpédition" (remplace "en attente de paiement")
   */
  const fillAdresseRetourMessage = () => {
    if (selectedDemandes.length === 0) return;

    let finalSubject = '';
    let finalBody = '';

    selectedDemandes.forEach((demande, index) => {
      const subjectPart = buildObject(demande, 'Adresse de réexpédition');
      finalSubject += subjectPart;
      if (index < selectedDemandes.length - 1) {
        finalSubject += ' | ';
      }

      const body = `
Bonjour ${demande.prenom},

Afin de préparer le retour de votre passeport, merci de bien vouloir nous faire parvenir par retour de mail l'adresse à laquelle vous souhaitez le recevoir.

Si vous avez choisi un retour par DHL, merci d'indiquer :
- Le nom et prénom du destinataire
- Si retour adresse professionnelle, nom de la société
- Adresse complète
- Le numéro de téléphone du destinataire
- L'adresse mail à laquelle vous souhaitez recevoir le suivi DHL

Si vous avez choisi un retour en livraison (Paris et proche banlieue uniquement)
- Le nom et prénom du destinataire
- Si retour adresse professionnelle, nom de la société
- Adresse complète (code porte, étage, bâtiment…)
- Le numéro de téléphone du destinataire

Vous recevrez l'information de la réexpédition dès réception du visa par nos services.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---

`;
      finalBody += body;
    });

    setObjetMail(finalSubject);
    setMessageMail(finalBody);
    setCurrentActionType('adresse_retour');
  };

  /**
   * Action "Dossier reçu" (pour visas uniquement, pas e-visas)
   * Change etat_visa = 'Reçu' et prépare le mail de confirmation
   */
  const fillDossierRecuMessage = () => {
    if (selectedDemandes.length === 0) return;

    // Construire l'objet et le message pour chaque demande
    let finalSubject = '';
    let finalBody = '';

    selectedDemandes.forEach((demande, index) => {
      const subjectPart = `${demande.nom}/${demande.prenom} - Dossier ${demande.id} - Dossier reçu`;
      finalSubject += subjectPart;
      if (index < selectedDemandes.length - 1) {
        finalSubject += ' | ';
      }

      const body = `Bonjour,

Nous accusons bonne réception de votre dossier et vous en remercions.

Nos agents prennent en charge votre demande, des mails de suivi vous seront adressés à chaque étape de votre commande.

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde
https://visamonde.com
89 boulevard national 92250 LA GARENNE COLOMBES (FRANCE)
(+33)1 86 22 08 06
contact@visamonde.com

---

`;
      finalBody += body;
    });

    setObjetMail(finalSubject);
    setMessageMail(finalBody);
    setCurrentActionType('dossier_recu');
  };

  /**
   * Mail avec signature standard (visa et evisa)
   * Juste pour envoyer un mail, sans modifier l'état du dossier
   */
  const fillSignatureMessage = () => {
    if (selectedDemandes.length === 0) return;

    let finalSubject = '';
    let finalBody = '';

    selectedDemandes.forEach((demande, index) => {
      const subjectPart = buildObject(demande, 'Information concernant votre dossier');
      finalSubject += subjectPart;
      if (index < selectedDemandes.length - 1) {
        finalSubject += ' | ';
      }

      const body = `
Bonjour ${demande.prenom},

[Votre message personnalisé ici]

Pour toute question, contactez-nous sur contact@visamonde.com

Cordialement,
L'équipe Visa Monde

<img src="https://ci3.googleusercontent.com/mail-sig/AIorK4z4GRH3V7qan5qlurDL6pCLND7O3V30v9b0KuMKCQ7QVYfS1CdbtJy00i9apP08aQbZOanAi9E" alt="Visa Monde" style="max-width: 200px; margin-top: 10px;">

https://visamonde.com
89 boulevard national
92250 LA GARENNE COLOMBES
(+33)1 86 22 08 06
contact@visamonde.com

---

`;
      finalBody += body;
    });

    setObjetMail(finalSubject);
    setMessageMail(finalBody);
    setCurrentActionType('mail_signature');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      maxWidth={false}
      fullWidth
      PaperProps={{
        // Sur mobile : plein écran (on neutralise le dimensionnement 95vw fixe).
        // Sur desktop : comportement existant inchangé.
        sx: isMobile
          ? undefined
          : {
              width: '95vw',
              height: '95vh',
              maxWidth: '95vw',
              maxHeight: '95vh',
              margin: '2.5vh'
            }
      }}
    >
      <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
        Actions pour les demandes sélectionnées
      </DialogTitle>
      <DialogContent sx={{ paddingTop: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            margin="dense"
            label="Email du client"
            type="email"
            variant="outlined"
            value={localEmail || ''}
            onChange={(e) => setLocalEmail(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
            <FormControlLabel
              control={<Checkbox checked={alerte} onChange={(e) => setAlerte(e.target.checked)} />}
              label="Alerte"
            />
            <FormControlLabel
              control={<Checkbox checked={envoyerMail} onChange={(e) => setEnvoyerMail(e.target.checked)} />}
              label="Envoyer mail"
            />
          </Box>
        </Box>

        <TextField
          margin="dense"
          label="Objet mail"
          type="text"
          fullWidth
          variant="outlined"
          value={objetMail}
          onChange={(e) => setObjetMail(e.target.value)}
        />

        <TextField
          margin="dense"
          label="Message Appli"
          type="text"
          fullWidth
          variant="outlined"
          value={messageAppli}
          onChange={(e) => setMessageAppli(e.target.value)}
          rows={2}
          multiline
        />

        <TextField
          margin="dense"
          label="Message Mail"
          multiline
          rows={20}
          fullWidth
          variant="outlined"
          value={messageMail}
          onChange={(e) => setMessageMail(e.target.value)}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              fontSize: '15px',
              fontFamily: 'monospace',
              lineHeight: '1.6'
            },
            '& textarea': {
              height: '100% !important'
            }
          }}
        />

        {/* Boutons pour pré-remplir les mails */}
        <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
          <Button onClick={fillProblemMessage} variant="contained" color="primary">
            Mail Visa(s) Problème
          </Button>
          <Button onClick={fillInProgressMessage} variant="contained" color="primary">
            Mail Visa(s) en Cours
          </Button>
        </Box>

        <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
          <Button onClick={fillVisaLaterMessage} variant="contained" color="primary">
            Mail Visa Plus Tard
          </Button>
          {/* Afficher le bouton Mail Adresse Retour uniquement si aucun eVisa dans la sélection */}
          {selectedDemandes.length > 0 &&
           !selectedDemandes.some(demande => isEvisa(demande.titre)) && (
            <Button onClick={fillAdresseRetourMessage} variant="contained" color="primary">
              Mail Adresse Retour
            </Button>
          )}
        </Box>

        <Box display="flex" justifyContent="center" gap={2} mt={2} flexWrap="wrap">
          {/* Afficher le bouton Dossier Reçu uniquement pour les visas classiques (pas e-visas) */}
          {selectedDemandes.length > 0 &&
           !selectedDemandes.some(demande => isEvisa(demande.titre)) && (
            <Button onClick={fillDossierRecuMessage} variant="contained" color="success">
              Dossier Reçu
            </Button>
          )}
          {/* Bouton Mail Signature (disponible pour tous: visa ET evisa) */}
          <Button onClick={fillSignatureMessage} variant="contained" color="info">
            Mail Signature
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleValidation} variant="contained">Valider</Button>
      </DialogActions>
    </Dialog>
  );
};

export default DemandeActions;
